import React from 'react';
import { Job, JobStatus } from '../../job-summary/Interfaces';
import { KTSVG } from '../../../../_metronic/helpers';
import { JobWithCustomer } from '../utils/customerListTypes';
import { getBadgeColor, getJobBadgeColor } from '../utils/statusHelpers';
import JobStatusDropdown from './JobStatusDropdown';

export interface JobCompactListProps {
  rows: JobWithCustomer[];
  formatTime: (minutes: number) => string;
  formatDueDate: (dueDate: string | undefined) => string;
  isJobActive: (job: Job) => boolean;
  currentSessionMinutes: number;
  showStatusDropdown: string | null;
  onJobClick: (e: React.MouseEvent<HTMLButtonElement>, job: Job) => void;
  onDateClick: (e: React.MouseEvent<HTMLElement>, job: Job) => void;
  onOpenStatusModal: (job: Job, e: React.MouseEvent) => void;
  onStatusDropdownClose: () => void;
  onStatusChange: (status: JobStatus) => void;
  onEditJob: (row: JobWithCustomer) => void;
  onOpenWorkspace: (row: JobWithCustomer) => void;
}

const JobCompactList: React.FC<JobCompactListProps> = ({
  rows,
  formatTime,
  formatDueDate,
  isJobActive,
  currentSessionMinutes,
  showStatusDropdown,
  onJobClick,
  onDateClick,
  onOpenStatusModal,
  onStatusDropdownClose,
  onStatusChange,
  onEditJob,
  onOpenWorkspace,
}) => {
  if (rows.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500 fs-7">
        No jobs match the current filters.
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2">
        <thead>
          <tr className="fw-bold text-muted fs-7 text-uppercase gs-0">
            <th className="min-w-140px ps-3">Customer</th>
            <th className="min-w-180px">Job</th>
            <th className="min-w-100px">Status</th>
            <th className="min-w-80px">Due</th>
            <th className="min-w-70px">Time</th>
            <th className="min-w-90px text-end pe-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ job, customer }) => {
            const totalMinutes = job.workSessions?.reduce((s, w) => s + w.durationMinutes, 0) || 0;
            const badgeColor = getBadgeColor(customer);
            const active = isJobActive(job);

            return (
              <tr
                key={job.id}
                className="cursor-pointer"
                style={{ transition: 'background-color 0.15s' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--bs-gray-100, rgba(0,0,0,0.04))';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '';
                }}
              >
                <td className="ps-3 py-2">
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="symbol symbol-32px symbol-circle"
                      style={{ flexShrink: 0 }}
                    >
                      <div
                        className="symbol-label fs-8 fw-bold text-white"
                        style={{ backgroundColor: badgeColor }}
                      >
                        {customer.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <span className="text-gray-800 fw-semibold fs-7 d-block text-truncate" style={{ maxWidth: '140px' }}>
                        {customer.name}
                      </span>
                      <span className="text-gray-500 fs-8 d-block text-truncate" style={{ maxWidth: '140px' }}>
                        {customer.email}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="py-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-light-primary btn-active-light-primary text-start w-100 border-0 py-1"
                    onClick={(e) => onJobClick(e, job)}
                  >
                    <span className="fw-semibold text-gray-800 fs-7 text-truncate d-inline-block" style={{ maxWidth: '160px' }} title={job.jobLabel}>
                      {job.jobLabel || `Job #${job.id.slice(0, 8)}`}
                    </span>
                  </button>
                </td>
                <td className="py-2" onClick={(e) => e.stopPropagation()}>
                  <JobStatusDropdown
                    job={job}
                    isJobActive={active}
                    currentSessionMinutes={currentSessionMinutes}
                    formatTime={formatTime}
                    show={showStatusDropdown === job.id}
                    onTriggerClick={(e) => {
                      e.stopPropagation();
                      if (showStatusDropdown === job.id) {
                        onStatusDropdownClose();
                      } else {
                        onOpenStatusModal(job, e);
                      }
                    }}
                    onStatusChange={onStatusChange}
                  />
                </td>
                <td className="py-2">
                  <span
                    role="button"
                    tabIndex={0}
                    className="badge fs-8 fw-semibold cursor-pointer"
                    style={{
                      backgroundColor: getJobBadgeColor(job, active),
                      color: 'white',
                    }}
                    onClick={(e) => onDateClick(e, job)}
                    title="Click to change due date"
                  >
                    {formatDueDate(job.dueDate)}
                  </span>
                </td>
                <td className="py-2">
                  {totalMinutes > 0 ? (
                    <span className="text-gray-600 fs-8">⏱️ {formatTime(totalMinutes)}</span>
                  ) : (
                    <span className="text-gray-400 fs-8">—</span>
                  )}
                </td>
                <td className="py-2 pe-3 text-end">
                  <div className="d-flex justify-content-end gap-1">
                    <button
                      type="button"
                      className="btn btn-icon btn-sm btn-light-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenWorkspace({ job, customer });
                      }}
                      title="Open workspace"
                    >
                      <KTSVG path="/media/icons/duotune/general/gen054.svg" className="svg-icon-5" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-icon btn-sm btn-light-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditJob({ job, customer });
                      }}
                      title="Edit job"
                    >
                      <KTSVG path="/media/icons/duotune/art/art005.svg" className="svg-icon-5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default JobCompactList;
